import merge from 'lodash/merge';
import { combineReducers } from 'redux';
import { normalize } from 'normalizr';

import { personaSchema } from '../../schemas';
import {
  GET_PERSONA_DAILY_ACTIVITIES_DETAILS_FULFILLED,
  GET_PERSONA_DAILY_ACTIVITIES_DETAILS_PENDING,
  GET_PERSONA_DAILY_ACTIVITIES_DETAILS_REJECTED,
  GET_PERSONA_INFO_FULFILLED,
  GET_PERSONA_INFO_PENDING,
  GET_PERSONA_INFO_REJECTED,
  GET_PERSONA_INTERESTS_FULFILLED,
  GET_PERSONA_INTERESTS_PENDING,
  GET_PERSONA_INTERESTS_REJECTED,
  GET_PERSONA_POSTS_FULFILLED,
  GET_PERSONA_POSTS_PENDING,
  GET_PERSONA_POSTS_REJECTED,
  GET_PERSONA_RECENT_ACTIVITIES_FULFILLED,
  GET_PERSONA_RECENT_ACTIVITIES_PENDING,
  GET_PERSONA_RECENT_ACTIVITIES_REJECTED,
} from '../../types';

// State interfaces
interface InfoState {
  id: string | null;
  entities: any;
  isFetching: boolean;
  error: any | null;
}

interface RecentActivitiesState {
  data: {
    date: any[];
    times: any[];
  };
  isFetching: boolean;
  error: any | null;
}

interface PostsState {
  persona: string | null;
  data: any[];
  total: number;
  offset: number;
  limit: number;
  isFetching: boolean;
  error: any | null;
}

interface InterestsState {
  data: any[];
  isFetching: boolean;
  error: any | null;
}

interface DailyActivitiesDetailsState {
  data: {
    posts: any[];
    comments: any[];
  };
  isFetching: boolean;
  error: any | null;
}

interface PersonaState {
  dailyActivitiesDetails: DailyActivitiesDetailsState;
  info: InfoState;
  interests: InterestsState;
  posts: PostsState;
  recentActivities: RecentActivitiesState;
}

// Action interfaces
interface PendingAction {
  type: typeof GET_PERSONA_INFO_PENDING | typeof GET_PERSONA_RECENT_ACTIVITIES_PENDING |
        typeof GET_PERSONA_POSTS_PENDING | typeof GET_PERSONA_INTERESTS_PENDING |
        typeof GET_PERSONA_DAILY_ACTIVITIES_DETAILS_PENDING;
}

interface RejectedAction {
  type: typeof GET_PERSONA_INFO_REJECTED | typeof GET_PERSONA_RECENT_ACTIVITIES_REJECTED |
        typeof GET_PERSONA_POSTS_REJECTED | typeof GET_PERSONA_INTERESTS_REJECTED |
        typeof GET_PERSONA_DAILY_ACTIVITIES_DETAILS_REJECTED;
  error: any;
}

interface GetPersonaInfoFulfilledAction {
  type: typeof GET_PERSONA_INFO_FULFILLED;
  payload: {
    data: any;
  };
}

interface GetPersonaRecentActivitiesFulfilledAction {
  type: typeof GET_PERSONA_RECENT_ACTIVITIES_FULFILLED;
  payload: {
    data: {
      date: any[];
      times: any[];
    };
  };
}

interface GetPersonaPostsFulfilledAction {
  type: typeof GET_PERSONA_POSTS_FULFILLED;
  payload: {
    data: {
      total: number;
      content: any[];
    };
  };
  meta: {
    persona: string;
    offset: number;
    limit: number;
  };
}

interface GetPersonaInterestsFulfilledAction {
  type: typeof GET_PERSONA_INTERESTS_FULFILLED;
  payload: {
    data: any[];
  };
}

interface GetPersonaDailyActivitiesDetailsFulfilledAction {
  type: typeof GET_PERSONA_DAILY_ACTIVITIES_DETAILS_FULFILLED;
  payload: {
    data: {
      posts: any[];
      comments: any[];
    };
  };
}

type InfoAction = PendingAction | GetPersonaInfoFulfilledAction | RejectedAction;
type RecentActivitiesAction = PendingAction | GetPersonaRecentActivitiesFulfilledAction | RejectedAction;
type PostsAction = PendingAction | GetPersonaPostsFulfilledAction | RejectedAction;
type InterestsAction = PendingAction | GetPersonaInterestsFulfilledAction | RejectedAction;
type DailyActivitiesDetailsAction = PendingAction | GetPersonaDailyActivitiesDetailsFulfilledAction | RejectedAction;

const INITIAL_STATE = {
  INFO: {
    id: null,
    entities: {},
    isFetching: false,
    error: null,
  },
  RECENT_ACTIVITIES: {
    data: {
      date: [],
      times: [],
    },
    isFetching: false,
    error: null,
  },
  POSTS: {
    persona: null,
    data: [],
    total: 0,
    offset: 0,
    limit: 0,
    isFetching: false,
    error: null,
  },
  INTERESTS: {
    data: [],
    isFetching: false,
    error: null,
  },
  DAILY_ACTIVITIES_DETAILS: {
    data: {
      posts: [],
      comments: [],
    },
    isFetching: false,
    error: null,
  },
};

const info = (state: InfoState = INITIAL_STATE.INFO, action: InfoAction): InfoState => {
  switch (action.type) {
    case GET_PERSONA_INFO_PENDING:
      return {
        ...state,
        isFetching: true,
      };
    case GET_PERSONA_INFO_FULFILLED: {
      const {
        data,
      } = action.payload;
      if (!data) {
        return INITIAL_STATE.INFO;
      }
      const {
        entities,
        result,
      } = normalize(data, personaSchema);
      return {
        entities: merge(state.entities, entities),
        id: result,
        isFetching: false,
        error: null,
      };
    }
    case GET_PERSONA_INFO_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const recentActivities = (state: RecentActivitiesState = INITIAL_STATE.RECENT_ACTIVITIES, action: RecentActivitiesAction): RecentActivitiesState => {
  switch (action.type) {
    case GET_PERSONA_RECENT_ACTIVITIES_PENDING:
      return {
        ...INITIAL_STATE.RECENT_ACTIVITIES,
        isFetching: true,
      };
    case GET_PERSONA_RECENT_ACTIVITIES_FULFILLED: {
      const {
        data,
      } = action.payload;
      return {
        data,
        isFetching: false,
        error: null,
      };
    }
    case GET_PERSONA_RECENT_ACTIVITIES_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const posts = (state: PostsState = INITIAL_STATE.POSTS, action: PostsAction): PostsState => {
  switch (action.type) {
    case GET_PERSONA_POSTS_PENDING:
      return {
        ...state,
        isFetching: true,
      };
    case GET_PERSONA_POSTS_FULFILLED: {
      const {
        persona,
        offset,
        limit,
      } = action.meta;
      const {
        data: {
          total,
          content,
        },
      } = action.payload;
      return {
        persona,
        data: content,
        total,
        offset,
        limit,
        isFetching: false,
        error: null,
      };
    }
    case GET_PERSONA_POSTS_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const interests = (state: InterestsState = INITIAL_STATE.INTERESTS, action: InterestsAction): InterestsState => {
  switch (action.type) {
    case GET_PERSONA_INTERESTS_PENDING:
      return {
        ...INITIAL_STATE.INTERESTS,
        isFetching: true,
      };
    case GET_PERSONA_INTERESTS_FULFILLED: {
      const {
        data,
      } = action.payload;
      return {
        data,
        isFetching: false,
        error: null,
      };
    }
    case GET_PERSONA_INTERESTS_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const dailyActivitiesDetails = (state: DailyActivitiesDetailsState = INITIAL_STATE.DAILY_ACTIVITIES_DETAILS, action: DailyActivitiesDetailsAction): DailyActivitiesDetailsState => {
  switch (action.type) {
    case GET_PERSONA_DAILY_ACTIVITIES_DETAILS_PENDING:
      return {
        ...INITIAL_STATE.DAILY_ACTIVITIES_DETAILS,
        isFetching: true,
      };
    case GET_PERSONA_DAILY_ACTIVITIES_DETAILS_FULFILLED:
      return {
        data: action.payload.data,
        isFetching: false,
        error: null,
      };
    case GET_PERSONA_DAILY_ACTIVITIES_DETAILS_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

export default combineReducers<PersonaState>({
  dailyActivitiesDetails,
  info,
  interests,
  posts,
  recentActivities,
});